import React, { useContext, useEffect, useState } from "react";
import { Row, Col, Form, InputGroup } from "react-bootstrap";
import { Controller } from "react-hook-form";
import Select from "react-select";
import JoditEditor from "jodit-react";
import classNames from "classnames";
import { Context } from "../../../utils/context";

// Shared by Add.js and Edit.js so the two forms can never drift apart again.

const plain = (html) => (html || "").replace(/<[^>]*>/g, "").trim();
const max200 = { maxLength: { value: 200, message: "Maximum 200 characters" } };
const whole = (msg) => ({ pattern: { value: /^\d+$/, message: msg } });
const money = (msg) => ({ pattern: { value: /^\d+(\.\d+)?$/, message: msg } });

// Loads the four dropdowns once
export const useCareerOptions = () => {
  const { getData, Select2Data } = useContext(Context);
  const [options, setOptions] = useState({});
  useEffect(() => {
    const load = async (key, url, field) => {
      const r = await getData(url);
      if (r?.success) {
        const list = await Select2Data(r?.data, field);
        setOptions((o) => ({ ...o, [key]: list }));
      }
    };
    load("jobtype", "/common/masters/allJobType", "job_type_id");
    load("shifttype", "/common/masters/allShift", "shift_type_id");
    load("qualification", "/common/masters/allQualification", "qualification_id");
    load("language", "/common/masters/allLanguage", "language_id");
    // eslint-disable-next-line
  }, []);
  return { options, today: new Date().toISOString().split("T")[0] };
};

// Turns the form values into the FormData the API expects
export const buildCareerFormData = (data) => {
  const fd = new FormData();
  [
    "name", "description", "skill", "job_location", "role_permission", "hr_name",
    "recruiter_email", "recruiter_contact_number", "start_annual_package",
    "end_annual_package", "company_name", "vacancy", "experience_from",
    "experience_to", "deadline",
  ].forEach((k) => fd.append(k, data?.[k] ?? ""));
  if (data?.job_type_id?.value) fd.append("job_type_id", data.job_type_id.value);
  if (data?.shift_type_id?.value) fd.append("shift_type_id", data.shift_type_id.value);
  fd.append("language_id", JSON.stringify((data?.language_id || []).map((o) => o.value)));
  fd.append("qualification_id", JSON.stringify((data?.qualification_id || []).map((o) => o.value)));
  // only a newly picked file; on Edit the form holds the old path string
  if (data?.image && typeof data.image !== "string" && data.image.length) fd.append("image", data.image[0]);
  return fd;
};

const Text = ({ f, name, label, rules, col = 6, ...rest }) => (
  <Col md={col}>
    <div className="main-form-section mt-3">
      <Form.Group>
        <Form.Label>{label}</Form.Label>
        <InputGroup>
          <Form.Control
            type="text"
            placeholder={label}
            className={classNames("", { "is-invalid": f.errors?.[name] })}
            {...f.register(name, rules)}
            {...rest}
          />
        </InputGroup>
        {f.errors?.[name] && <span className="text-danger">{f.errors[name].message}</span>}
      </Form.Group>
    </div>
  </Col>
);

const Rich = ({ f, name, label, message }) => (
  <Col md={12}>
    <div className="main-form-section mt-3">
      <Form.Label>{label}</Form.Label>
      <Controller
        name={name}
        control={f.control}
        defaultValue=""
        rules={{ validate: (v) => plain(v).length > 0 || message }}
        render={({ field: { onChange, onBlur, value } }) => (
          <JoditEditor
            value={value || ""}
            onBlur={() => {
              onBlur();
              f.trigger(name);
            }}
            onChange={onChange}
          />
        )}
      />
      {f.errors?.[name] && <span className="text-danger">{f.errors[name].message}</span>}
    </div>
  </Col>
);

const Pick = ({ f, name, label, options, isMulti, required }) => (
  <Col md={6}>
    <div className="main-form-section mt-3">
      <Form.Label>{label}</Form.Label>
      <Controller
        name={name}
        control={f.control}
        rules={required ? { validate: (v) => (isMulti ? v?.length > 0 : !!v) || required } : {}}
        render={({ field }) => (
          <Select
            {...field}
            isMulti={isMulti}
            isClearable={!isMulti}
            options={options}
            styles={{
              control: (base) => ({ ...base, borderColor: f.errors?.[name] ? "red" : base.borderColor }),
            }}
          />
        )}
      />
      {f.errors?.[name] && <span className="text-danger">{f.errors[name].message}</span>}
    </div>
  </Col>
);

// File input + preview. `value` is watch("image"): a FileList, or the stored path on Edit.
export const ImageField = ({ register, value }) => {
  const { IMG_URL } = useContext(Context);
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (value && typeof value !== "string" && value.length) {
      const u = URL.createObjectURL(value[0]);
      setUrl(u);
      return () => URL.revokeObjectURL(u);
    }
    setUrl(typeof value === "string" && value ? IMG_URL + value : "");
    return undefined;
  }, [value, IMG_URL]);
  return (
    <>
      <Col md={6}>
        <div className="main-form-section mt-3">
          <Form.Group>
            <Form.Label>Image</Form.Label>
            <Form.Control type="file" accept=".jpg,.jpeg,.png" {...register("image")} />
          </Form.Group>
        </div>
      </Col>
      <Col md={6}>
        <div className="main-form-section mt-3">
          {url && <img src={url} alt="Preview" className="image-preview" style={{ maxWidth: 140 }} />}
        </div>
      </Col>
    </>
  );
};

const CareerFields = ({ f, options, today, children }) => (
  <Row>
    <Text f={f} name="name" label="Name" rules={{ required: "Name is required", ...max200 }} />
    <Text f={f} name="skill" label="Skill" rules={max200} />
    <Rich f={f} name="description" label="Description" message="Description is required." />
    <Rich f={f} name="role_permission" label="Role-Permission" message="Role permission is required." />
    <Text f={f} name="job_location" label="Job Location" rules={max200} />
    <Text f={f} name="hr_name" label="HR Name" rules={max200} />
    <Pick f={f} name="language_id" label="Languages" options={options.language} isMulti required="Select at least one language" />
    <Pick f={f} name="qualification_id" label="Qualification" options={options.qualification} isMulti required="Select at least one qualification" />
    <Pick f={f} name="job_type_id" label="Job Type" options={options.jobtype} />
    <Pick f={f} name="shift_type_id" label="Shift Type" options={options.shifttype} />
    <Text
      f={f}
      name="recruiter_email"
      label="Recruiter Email"
      rules={{
        ...max200,
        pattern: { value: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/, message: "Email Id Invalid" },
      }}
    />
    <Text
      f={f}
      name="recruiter_contact_number"
      label="Recruiter Contact"
      rules={{ pattern: { value: /^\+?\d[\d\s-]{8,14}\d$/, message: "Enter a valid phone number" } }}
    />
    <Text f={f} name="start_annual_package" label="Start Annual Package" rules={money("Must be a number")} />
    <Text f={f} name="end_annual_package" label="End Annual Package" rules={money("Must be a number")} />
    <Text f={f} name="company_name" label="Company Name" rules={max200} />
    <Text f={f} name="vacancy" label="Vacancy" rules={whole("Must be a whole number")} />
    <Text f={f} name="experience_from" label="Experience From (years)" rules={whole("Must be a whole number")} />
    <Text f={f} name="experience_to" label="Experience To (years)" rules={whole("Must be a whole number")} />
    <Text f={f} name="deadline" label="Deadline" type="date" min={today} />
    {children}
  </Row>
);

export default CareerFields;